/**
 * Browse > Basecamp > Activity - webhook event log with Continuity gestures.
 * Select + secondary preview, double-click open, right-click menu (ADR-0020).
 */

import { useCallback, useEffect, useMemo, useState, type MouseEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Panel, PanelGroup } from 'react-resizable-panels'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Check,
  ChevronDown,
  ListTodo,
  Loader2,
  Search,
  Radio,
  SlidersHorizontal,
} from 'lucide-react'
import { fetchWithAuth } from '@/lib/api'
import { useBasecampAuth, useBasecampProjects } from '@/hooks/use-basecamp'
import { openBasecampListeningSettings } from '@/lib/open-settings'
import { isModClick } from '@/lib/open-intent'
import { OpenDestinationIcons, resolveOpenExternalIcon } from '@/lib/open-destination-icons'
import { buildBasecampWebUrl } from '@/lib/basecamp-external-url'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'
import {
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_CLASS,
  QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS,
} from '@/lib/menu-chrome'
import {
  ExtensionFilterBar,
  ExtensionListRow,
  ExtensionPanePlaceholder,
  IndexPreviewEmpty,
  IndexSurfaceShell,
  SecondaryPreviewResizeHandle,
  useRegisterIndexPreviewPin,
} from '@/components/integrations/_shared'
import { ListSurfaceStatusBar } from '@/components/ui/list-surface-status-bar'
import { EntityContextMenu } from '@/components/features/entity-context-menu'
import { PreviewPaneHeader } from '@/components/features/preview-pane-header'
import { BasecampTodoPreviewPane } from './basecamp-todo-preview-pane'
import { ConnectState } from './basecamp-section-view'
import { BasecampIcon } from '@/components/icons/basecamp-icon'
import { toast } from 'sonner'
import type { BasecampAssignment } from '@/types/basecamp'
import { usePersistedState } from '@/hooks/use-persisted-state'
import { StorageKeys, StorageSchemas } from '@/lib/storage'
import {
  activityViewFromSearchParams,
  DEFAULT_ACTIVITY_VIEW,
  type ActivityViewState,
} from '@/lib/basecamp-view-persist'
import { activityQueryFromView, appendQuery, isBasecampActivityPath } from '@/lib/index-query-url'
import {
  INDEX_VIEW_ACTION_EVENT,
  indexViewActionFromEvent,
} from '@/lib/index-surface-actions'
import { activityEventMatchesQuery } from '@/lib/basecamp-activity-search'
import { PanelSearchBar } from '@/components/ui/panel-search-bar'
import { usePanelSearch } from '@/hooks/use-panel-search'
import { panelSearchMenuAction } from '@/lib/panel-search-menu'

type Who = 'org' | 'team' | 'mine'
type KindFilter = 'all' | 'comments' | 'completions' | 'updates'

interface WebhookEvent {
  id: string
  kind: string
  project_id: string | null
  recording_id: string | null
  recording_type: string | null
  todo_id: string | null
  actor_name: string | null
  actor_is_org_member: boolean | null
  occurred_at: string
  title: string | null
  excerpt: string | null
  has_work_spec: boolean | null
}

interface ProjectOption {
  id: string
  name: string
}

interface BasecampActivityFeedProps {
  organizationId?: string
}

const ACTIVITY_PREVIEW_OPEN_KEY = 'flownotes-basecamp-activity-preview-open'
const ACTIVITY_PAGE_SIZE = 80

/** Decode common HTML entities in webhook excerpts (Basecamp often sends &nbsp;). */
function decodeExcerpt(raw: string | null | undefined): string {
  if (!raw) return ''
  return raw
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim()
}

/** MaaterSegmentControl language in production tokens (maater-ui active segment). */
function SegmentTrack<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
  testId,
}: {
  value: T
  onChange: (next: T) => void
  options: { id: T; label: string }[]
  ariaLabel: string
  testId?: string
}) {
  return (
    <div
      className="inline-flex flex-wrap gap-1 rounded-[10px] bg-muted p-[3px]"
      role="tablist"
      aria-label={ariaLabel}
      data-testid={testId}
    >
      {options.map((opt) => {
        const active = opt.id === value
        return (
          <button
            key={opt.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt.id)}
            className={cn(
              'rounded-md border px-3 py-1.5 text-xs font-medium transition-colors',
              active
                ? 'border-primary bg-background text-foreground shadow-sm'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}


function kindDot(kind: string) {
  if (kind === 'comment_created') return 'bg-blue-500'
  if (kind === 'todo_completed') return 'bg-green-500'
  if (kind.includes('assignment')) return 'bg-yellow-500'
  return 'bg-muted-foreground'
}

function verbFor(kind: string) {
  if (kind === 'comment_created') return 'commented on'
  if (kind === 'todo_completed') return 'completed'
  if (kind === 'todo_created') return 'created'
  if (kind === 'todo_assignment_changed') return 'changed assignees on'
  return kind.replace(/_/g, ' ')
}

function dayKey(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function eventTodoId(ev: WebhookEvent): string | null {
  return ev.todo_id || (ev.recording_type === 'Todo' ? ev.recording_id : null)
}

function assignmentFromEvent(
  ev: WebhookEvent,
  projectName: string,
): BasecampAssignment | null {
  const todoId = eventTodoId(ev)
  if (!todoId || !ev.project_id) return null
  return {
    id: todoId,
    title: (ev.title || 'Todo').replace(/^Re:\s*/i, ''),
    url: '',
    completed: ev.kind === 'todo_completed',
    project: { id: ev.project_id, name: projectName },
    assignee: { id: '', name: '' },
    _project_name: projectName,
    _basecamp_project_id: ev.project_id,
    _app_url: buildBasecampWebUrl(todoId, ev.project_id, 'todo') || undefined,
    _last_comment_text: ev.kind === 'comment_created' ? ev.excerpt : null,
  }
}

function openTodoTab(
  todoId: string,
  title: string,
  projectId: string,
  projectName: string,
  organizationId: string | undefined,
  opts?: { openInNewTab?: boolean; event?: MouseEvent },
) {
  const openInNewTab =
    opts?.openInNewTab === true || (opts?.event ? isModClick(opts.event) : false)
  window.dispatchEvent(
    new CustomEvent('integration-view-change', {
      detail: {
        tabId: `basecamp-todo-${todoId}`,
        title: title || 'Todo',
        type: 'integration-basecamp-todo',
        openInNewTab,
        integrationData: {
          integrationId: 'basecamp',
          projectId,
          todoId,
          projectName,
          ...(organizationId ? { organizationId } : {}),
        },
      },
    }),
  )
}

function ProjectFilterCombobox({
  projects,
  value,
  onChange,
}: {
  projects: ProjectOption[]
  value: string
  onChange: (id: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return projects
    return projects.filter(
      (p) => p.name.toLowerCase().includes(needle) || p.id.includes(needle),
    )
  }, [projects, query])

  const selected = projects.find((p) => p.id === value)
  const label =
    selected?.name ??
    (projects.length ? `All projects (${projects.length})` : 'All projects')

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) setQuery('')
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="ml-auto h-8 max-w-[240px] justify-between gap-2 text-xs font-normal"
          data-testid={testIds.basecamp.activityProjectFilter}
          aria-label="Filter by project"
        >
          <span className="truncate">{label}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className={cn(QUIET_OVERFLOW_MENU_CONTENT_CLASS, 'w-72 p-0')}
        collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div
          className="flex items-center gap-2 border-b border-border px-2 py-1.5"
          onKeyDown={(e) => e.stopPropagation()}
        >
          <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter projects..."
            className="h-7 min-w-0 flex-1 bg-transparent text-xs outline-none placeholder:text-muted-foreground/50"
            data-testid={testIds.basecamp.activityProjectFilterSearch}
            autoFocus
          />
        </div>
        <div className="max-h-60 overflow-y-auto p-1">
          <DropdownMenuItem className="gap-2 text-xs" onSelect={() => onChange('')}>
            <Check className={cn('h-3.5 w-3.5', value ? 'opacity-0' : 'opacity-100')} />
            <span>All projects</span>
          </DropdownMenuItem>
          {filtered.map((p) => (
            <DropdownMenuItem
              key={p.id}
              className="gap-2 text-xs"
              onSelect={() => onChange(p.id)}
            >
              <Check
                className={cn(
                  'h-3.5 w-3.5 shrink-0',
                  value === p.id ? 'opacity-100' : 'opacity-0',
                )}
              />
              <span className="truncate">{p.name}</span>
            </DropdownMenuItem>
          ))}
          {projects.length === 0 ? (
            <p className="px-2 py-3 text-xs text-muted-foreground">
              No listening projects yet. Enable Listening in Settings first.
            </p>
          ) : filtered.length === 0 ? (
            <p className="px-2 py-3 text-xs text-muted-foreground">No matching projects</p>
          ) : null}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function BasecampActivityFeed({ organizationId }: BasecampActivityFeedProps) {
  const { authStatus } = useBasecampAuth(organizationId)
  const location = useLocation()
  const navigate = useNavigate()
  const { projects: basecampProjects } = useBasecampProjects(organizationId)
  const [events, setEvents] = useState<WebhookEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [activityView, setActivityView] = usePersistedState<ActivityViewState>({
    key: StorageKeys.BASECAMP_ACTIVITY_VIEW(organizationId),
    defaultValue: DEFAULT_ACTIVITY_VIEW,
    schema: StorageSchemas.basecampActivityView,
  })
  const who = activityView.who
  const kind = activityView.kind
  const linkedOnly = activityView.linkedOnly
  const projectId = activityView.projectId
  const setWho = (next: Who) => setActivityView((v) => ({ ...v, who: next }))
  const setKind = (next: KindFilter) => setActivityView((v) => ({ ...v, kind: next }))
  const setLinkedOnly = (next: boolean | ((prev: boolean) => boolean)) =>
    setActivityView((v) => ({
      ...v,
      linkedOnly: typeof next === 'function' ? next(v.linkedOnly) : next,
    }))
  const setProjectId = (next: string) => setActivityView((v) => ({ ...v, projectId: next }))
  const {
    searchOpen,
    searchQuery: query,
    setSearchQuery: setQuery,
    inputRef: activitySearchRef,
    close: closeActivitySearch,
    open: openActivitySearch,
  } = usePanelSearch({ initialQuery: activityView.q || '' })
  const [activityUrlReady, setActivityUrlReady] = useState(false)
  useEffect(() => {
    if (!isBasecampActivityPath(location.pathname)) return
    const next = activityViewFromSearchParams(new URLSearchParams(location.search))
    if (next) setActivityView((prev) => ({ ...next, q: prev.q }))
    setActivityUrlReady(true)
  }, [location.pathname, location.search, setActivityView])
  useEffect(() => {
    if (!activityUrlReady) return
    if (!isBasecampActivityPath(location.pathname)) return
    const next = appendQuery(location.pathname, activityQueryFromView(activityView))
    const current = `${location.pathname}${location.search || ''}`
    if (next !== current) navigate(next, { replace: true })
  }, [activityUrlReady, activityView, location.pathname, location.search, navigate])
  useEffect(() => {
    if ((activityView.q || '') === query) return
    setActivityView((v) => ({ ...v, q: query }))
  }, [query, activityView.q, setActivityView])
  const [listeningIds, setListeningIds] = useState<string[]>([])
  const [subscriptionNames, setSubscriptionNames] = useState<Map<string, string>>(
    new Map(),
  )
  const [health, setHealth] = useState<string>('disconnected')
  const [activeCount, setActiveCount] = useState(0)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(() => {
    try {
      return localStorage.getItem(ACTIVITY_PREVIEW_OPEN_KEY) !== 'false'
    } catch {
      return true
    }
  })
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null)
  const [ctx, setCtx] = useState<{
    x: number
    y: number
    event: WebhookEvent
  } | null>(null)

  const ExternalIcon = resolveOpenExternalIcon(BasecampIcon)

  const projects = useMemo((): ProjectOption[] => {
    const nameById = new Map<string, string>()
    for (const [id, name] of subscriptionNames) nameById.set(id, name)
    for (const p of basecampProjects) nameById.set(String(p.id), p.name)

    return listeningIds
      .map((id) => ({
        id,
        name: nameById.get(id) || `Project ${id}`,
      }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [listeningIds, basecampProjects, subscriptionNames])

  const projectNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const p of projects) map.set(p.id, p.name)
    for (const p of basecampProjects) map.set(String(p.id), p.name)
    return map
  }, [projects, basecampProjects])

  const refreshStatus = useCallback(async () => {
    if (!organizationId) return
    const statusRes = await fetchWithAuth(
      `/api/basecamp/webhook/status?organizationId=${encodeURIComponent(organizationId)}`,
      { headers: { 'x-organization-id': organizationId } },
    )
    const statusData = await statusRes.json()
    if (!statusRes.ok || !statusData.success) return
    setHealth(statusData.health || 'disconnected')
    const activeSubs = (
      (statusData.subscriptions || []) as {
        active: boolean
        bucket_id: string
        project_name: string | null
      }[]
    ).filter((s) => s.active)

    const ids: string[] = []
    const seen = new Set<string>()
    const names = new Map<string, string>()
    for (const s of activeSubs) {
      const id = String(s.bucket_id)
      if (seen.has(id)) continue
      seen.add(id)
      ids.push(id)
      if (s.project_name) names.set(id, s.project_name)
    }
    setListeningIds(ids)
    setSubscriptionNames(names)
    setActiveCount(ids.length || statusData.active_count || 0)
  }, [organizationId])

  const fetchEvents = useCallback(
    async (opts?: { before?: string; append?: boolean }) => {
      if (!organizationId) {
        setLoading(false)
        return
      }
      const appending = Boolean(opts?.append)
      if (appending) setLoadingMore(true)
      else setLoading(true)
      try {
        if (!appending) await refreshStatus()

        const params = new URLSearchParams({
          organizationId,
          who,
          kind,
          limit: String(ACTIVITY_PAGE_SIZE),
        })
        if (linkedOnly) params.set('linked_only', 'true')
        if (projectId) params.set('project_id', projectId)
        if (opts?.before) params.set('before', opts.before)

        const res = await fetchWithAuth(`/api/basecamp/webhook/events?${params}`, {
          headers: { 'x-organization-id': organizationId },
        })
        const data = await res.json()
        if (!res.ok || !data.success) {
          throw new Error(data.error || 'Failed to load events')
        }
        const next = (data.events || []) as WebhookEvent[]
        setHasMore(next.length >= ACTIVITY_PAGE_SIZE)
        setEvents((prev) => {
          if (!appending) return next
          const seen = new Set(prev.map((e) => e.id))
          return [...prev, ...next.filter((e) => !seen.has(e.id))]
        })
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Activity load failed')
      } finally {
        setLoading(false)
        setLoadingMore(false)
      }
    },
    [organizationId, who, kind, linkedOnly, projectId, refreshStatus],
  )

  useEffect(() => {
    void fetchEvents()
    const t = setInterval(() => void fetchEvents(), 60000)
    return () => clearInterval(t)
  }, [fetchEvents])

  useEffect(() => {
    const items = events.slice(0, ACTIVITY_PAGE_SIZE).map((ev) => ({
      occurredAt: ev.occurred_at,
      kind: ev.kind,
      actor: ev.actor_name || '',
      title: (ev.title || '').replace(/^Re:\s*/i, ''),
      projectName: projectNameById.get(String(ev.project_id || '')) || '',
      projectId: ev.project_id || '',
      todoId: eventTodoId(ev),
      excerpt: decodeExcerpt(ev.excerpt).slice(0, 140),
      hasWorkSpec: ev.has_work_spec,
    }))
    window.dispatchEvent(
      new CustomEvent('basecamp-activity-context-updated', {
        detail: {
          who,
          kind,
          linkedOnly,
          projectId: projectId || undefined,
          items,
          loadedAt: new Date().toISOString(),
        },
      }),
    )
  }, [events, who, kind, linkedOnly, projectId, projectNameById])

  useEffect(() => {
    if (projectId && !listeningIds.includes(projectId)) {
      setProjectId('')
    }
  }, [projectId, listeningIds])

  useEffect(() => {
    try {
      localStorage.setItem(ACTIVITY_PREVIEW_OPEN_KEY, previewOpen ? 'true' : 'false')
    } catch {
      /* ignore */
    }
  }, [previewOpen])

  const togglePreview = useCallback(() => setPreviewOpen((v) => !v), [])
  useRegisterIndexPreviewPin({
    open: previewOpen,
    onToggle: togglePreview,
    testId: testIds.basecamp.activityPreviewToggle,
  })

  const loadMore = () => {
    const oldest = events[events.length - 1]
    if (!oldest || loadingMore) return
    void fetchEvents({ before: oldest.occurred_at, append: true })
  }

  const openBasecampActivity = () => {
    // Basecamp has no single org-wide activity URL; open the filtered project or first listening project.
    const pid = projectId || listeningIds[0]
    if (!pid) {
      toast.message('Enable Listening on a project first')
      return
    }
    window.open(
      `https://app.basecamp.com/5744549/projects/${pid}`,
      '_blank',
      'noopener,noreferrer',
    )
  }

  const visibleEvents = useMemo(() => {
    return events.filter((ev) =>
      activityEventMatchesQuery(query, {
        actor_name: ev.actor_name,
        title: ev.title,
        excerpt: decodeExcerpt(ev.excerpt),
        projectName: projectNameById.get(String(ev.project_id || '')) || '',
      }),
    )
  }, [events, query, projectNameById])

  const grouped = useMemo(() => {
    const map = new Map<string, WebhookEvent[]>()
    for (const ev of visibleEvents) {
      const key = dayKey(ev.occurred_at)
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(ev)
    }
    return [...map.entries()]
  }, [visibleEvents])

  const selectedEvent =
    events.find((e) => e.id === selectedEventId) ?? null
  const selectedProjectName = selectedEvent?.project_id
    ? projectNameById.get(selectedEvent.project_id) ||
      `Project ${selectedEvent.project_id}`
    : ''
  const selectedAssignment = selectedEvent
    ? assignmentFromEvent(selectedEvent, selectedProjectName)
    : null

  const openListening = () => {
    openBasecampListeningSettings(organizationId)
  }

  useEffect(() => {
    const onRefresh = (e: Event) => {
      const section = (e as CustomEvent<{ section?: string }>).detail?.section
      if (!section || section === 'activity') void fetchEvents()
    }
    window.addEventListener('basecamp-section-refresh', onRefresh)
    return () => window.removeEventListener('basecamp-section-refresh', onRefresh)
  }, [fetchEvents])

  useEffect(() => {
    const onAction = (e: Event) => {
      const action = indexViewActionFromEvent(e)
      if (action === 'listening') openListening()
      if (action === 'open-external') openBasecampActivity()
    }
    window.addEventListener(INDEX_VIEW_ACTION_EVENT, onAction)
    return () => window.removeEventListener(INDEX_VIEW_ACTION_EVENT, onAction)
  }, [organizationId, projectId, listeningIds])

  const selectEvent = (ev: WebhookEvent) => {
    setSelectedEventId(ev.id)
    setPreviewOpen(true)
  }

  const openEvent = (
    ev: WebhookEvent,
    opts?: { openInNewTab?: boolean; event?: MouseEvent },
  ) => {
    const todoId = eventTodoId(ev)
    if (!todoId || !ev.project_id) {
      toast.message('This event is not linked to a todo')
      return
    }
    const name = projectNameById.get(ev.project_id) || `Project ${ev.project_id}`
    openTodoTab(
      todoId,
      ev.title || 'Todo',
      ev.project_id,
      name,
      organizationId,
      opts,
    )
  }

  const openEventInBasecamp = (ev: WebhookEvent) => {
    const todoId = eventTodoId(ev)
    if (!todoId || !ev.project_id) {
      toast.message('No Basecamp link for this event')
      return
    }
    const url = buildBasecampWebUrl(todoId, ev.project_id, 'todo')
    if (url) window.open(url, '_blank', 'noopener,noreferrer')
  }

  const activityFiltered =
    who !== DEFAULT_ACTIVITY_VIEW.who ||
    kind !== DEFAULT_ACTIVITY_VIEW.kind ||
    linkedOnly !== DEFAULT_ACTIVITY_VIEW.linkedOnly ||
    Boolean(projectId) ||
    Boolean(query.trim())
  const clearActivityFilters = () => {
    setWho(DEFAULT_ACTIVITY_VIEW.who)
    setKind(DEFAULT_ACTIVITY_VIEW.kind)
    setLinkedOnly(DEFAULT_ACTIVITY_VIEW.linkedOnly)
    setProjectId('')
    setQuery('')
  }

  if (authStatus && !authStatus.authenticated) {
    return (
      <div className="relative h-full">
        <ConnectState authUrl={authStatus.auth_url} organizationId={organizationId} />
      </div>
    )
  }

  return (
    <IndexSurfaceShell
      data-testid={testIds.basecamp.activityFeed}
      search={
        searchOpen ? (
        <PanelSearchBar
          inputRef={activitySearchRef}
          value={query}
          onChange={setQuery}
          onClose={closeActivitySearch}
          placeholder="filter by title, person, project..."
          resultCount={visibleEvents.length}
          totalCount={events.length}
          countNoun="events"
          data-testid={testIds.basecamp.activitySearch}
        />
        ) : null
      }
    >
      <PanelGroup direction="horizontal" className="flex min-h-0 flex-1">
        <Panel minSize={35} className="flex min-h-0 min-w-0 flex-col overflow-hidden">
          <ExtensionFilterBar
            singleRow
            search=""
            onSearchChange={() => undefined}
            showSearch={false}
            chipStyle="segment"
            chips={[
              { id: 'org', label: 'Org' },
              { id: 'team', label: 'Team' },
              { id: 'mine', label: 'Mine' },
            ]}
            activeChipId={who}
            onChipChange={(id) => setWho(id as Who)}
            trailing={
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 gap-1 text-xs font-normal"
                      aria-label="More filters"
                    >
                      <SlidersHorizontal className="h-3 w-3" />
                      Filters
                      <ChevronDown className="h-3 w-3 opacity-50" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    className={QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS}
                  >
                    <DropdownMenuLabel className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      Kind
                    </DropdownMenuLabel>
                    {([
                      ['all', 'All'],
                      ['comments', 'Comments'],
                      ['completions', 'Completions'],
                      ['updates', 'Updates'],
                    ] as const).map(([id, label]) => (
                      <DropdownMenuItem key={id} onClick={() => setKind(id)}>
                        <span className="flex-1">{label}</span>
                        {kind === id ? <Check className="h-3.5 w-3.5" /> : null}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setLinkedOnly((v) => !v)}>
                      <span className="flex-1">Linked only</span>
                      {linkedOnly ? <Check className="h-3.5 w-3.5" /> : null}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <ProjectFilterCombobox
                  projects={projects}
                  value={projectId}
                  onChange={setProjectId}
                />
                <span className="ml-auto inline-flex min-w-0 items-center gap-2 text-[11px] text-muted-foreground">
                  <span className="tabular-nums">
                    {query.trim()
                      ? `${visibleEvents.length} of ${events.length}`
                      : `${visibleEvents.length} shown`}
                  </span>
                  {activityFiltered ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 px-1.5 text-[11px] text-muted-foreground hover:text-foreground"
                      onClick={clearActivityFilters}
                    >
                      Clear
                    </Button>
                  ) : null}
                </span>
              </>
            }
          />
          <div className="relative min-h-0 flex-1 overflow-y-auto">
            {activeCount === 0 && !loading ? (
              <ExtensionPanePlaceholder
                variant="empty"
                title="Listening is off"
                description="Enable projects in Settings to receive Basecamp events here."
                testId={testIds.basecamp.panePlaceholder}
                action={
                  <Button size="sm" onClick={openListening}>
                    Open Listening settings
                  </Button>
                }
              />
            ) : loading && events.length === 0 ? (
              <ExtensionPanePlaceholder
                variant="loading"
                title="Loading activity..."
                testId={testIds.basecamp.panePlaceholder}
              />
            ) : events.length === 0 ? (
              <ExtensionPanePlaceholder
                variant="empty"
                title="No events yet"
                description="Comment on a todo (or complete one) in a listening project - it will show up here."
                testId={testIds.basecamp.panePlaceholder}
              />
            ) : visibleEvents.length === 0 ? (
              <ExtensionPanePlaceholder
                variant="empty"
                title="No events match."
                description="Clear the search filter to see all events."
                testId={testIds.basecamp.panePlaceholder}
              />
            ) : (
              grouped.map(([day, rows]) => {
                const people = new Set(rows.map((r) => r.actor_name).filter(Boolean))
                return (
                  <div key={day}>
                    <div className="sticky top-0 z-[1] flex items-baseline justify-between gap-3 border-b border-border bg-background/95 px-6 py-2 backdrop-blur">
                      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {day}
                      </h3>
                      <span className="text-xs text-muted-foreground">
                        {people.size} {people.size === 1 ? 'person' : 'people'} active
                      </span>
                    </div>
                    <ul>
                      {rows.map((ev) => {
                        const todoId = eventTodoId(ev)
                        const canOpen = Boolean(todoId && ev.project_id)
                        return (
                          <li key={ev.id}>
                            <ExtensionListRow
                              selected={
                                selectedEventId === ev.id && previewOpen
                              }
                              testId={testIds.basecamp.activityRow(ev.id)}
                              onSelect={(e) => {
                                if (e && isModClick(e) && canOpen) {
                                  openEvent(ev, { event: e })
                                  return
                                }
                                selectEvent(ev)
                              }}
                              onOpen={(e) => openEvent(ev, { event: e })}
                              onContextMenu={(e) => {
                                setSelectedEventId(ev.id)
                                setCtx({ x: e.clientX, y: e.clientY, event: ev })
                              }}
                            >
                              <span
                                className={cn(
                                  'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                                  kindDot(ev.kind),
                                )}
                              />
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                                  <span className="text-xs text-muted-foreground">
                                    {new Date(ev.occurred_at).toLocaleTimeString([], {
                                      hour: 'numeric',
                                      minute: '2-digit',
                                    })}
                                  </span>
                                  <span className="text-sm">
                                    <span className="font-medium">
                                      {ev.actor_name || 'Someone'}
                                    </span>{' '}
                                    {verbFor(ev.kind)}{' '}
                                    <span className="font-medium">
                                      {ev.title || 'a recording'}
                                    </span>
                                  </span>
                                  {ev.has_work_spec ? (
                                    <Badge
                                      variant="outline"
                                      className="border-primary/40 bg-primary/10 text-[10px] text-foreground"
                                    >
                                      Linked
                                    </Badge>
                                  ) : null}
                                </div>
                                {ev.excerpt ? (
                                  <p className="mt-0.5 truncate text-xs text-muted-foreground">
                                    {decodeExcerpt(ev.excerpt)}
                                  </p>
                                ) : null}
                              </div>
                            </ExtensionListRow>
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                )
              })
            )}
            {hasMore && events.length > 0 ? (
              <div className="flex justify-center px-6 py-3">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs"
                  disabled={loadingMore}
                  onClick={loadMore}
                >
                  {loadingMore ? (
                    <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                  ) : null}
                  Load older events
                </Button>
              </div>
            ) : null}
          </div>
          <ListSurfaceStatusBar
            includePreviewTip
            leading={
              <Badge variant="outline" className="capitalize text-[10px]">
                {health}
              </Badge>
            }
            trailing={
              <>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 hover:text-foreground"
                  onClick={openListening}
                  title="Listening settings"
                >
                  <Radio className="h-3 w-3" />
                  {activeCount} listening
                </button>
                <span>
                  {visibleEvents.length} from Listening cache
                  {hasMore ? ' · more available' : ''}
                </span>
              </>
            }
          />
        </Panel>

        {previewOpen ? (
          <>
            <SecondaryPreviewResizeHandle />
            <Panel
              defaultSize={32}
              minSize={22}
              maxSize={45}
              className="flex min-h-0 flex-col overflow-hidden"
              data-testid={testIds.basecamp.activitySecondary}
            >
              {selectedAssignment ? (
                <BasecampTodoPreviewPane
                  headerTestId={testIds.basecamp.activityPreviewHeader}
                  assignment={selectedAssignment}
                  onOpen={(event) => selectedEvent && openEvent(selectedEvent, { event })}
                  onClose={() => {
                    setPreviewOpen(false)
                    setSelectedEventId(null)
                  }}
                  onOpenBasecamp={(url) =>
                    window.open(url, '_blank', 'noopener,noreferrer')
                  }
                />
              ) : selectedEvent ? (
                <div className="flex h-full min-h-0 flex-col bg-card">
                <PreviewPaneHeader
                  row
                  label="Activity"
                  onClose={() => {
                    setPreviewOpen(false)
                    setSelectedEventId(null)
                  }}
                />
                <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center text-sm text-muted-foreground">
                  <ListTodo className="h-8 w-8 opacity-40" />
                  <p>This event is not linked to a todo</p>
                  <p className="text-xs">
                    {verbFor(selectedEvent.kind)} · {selectedEvent.title || 'Untitled'}
                  </p>
                </div>
                </div>
              ) : (
                <IndexPreviewEmpty
                  row
                  noun="activity item"
                  testId={testIds.basecamp.activityPreviewEmpty}
                  onClose={() => {
                    setPreviewOpen(false)
                    setSelectedEventId(null)
                  }}
                />
              )}
            </Panel>
          </>
        ) : null}
      </PanelGroup>

      {ctx ? (
        <EntityContextMenu
          x={ctx.x}
          y={ctx.y}
          data-testid={testIds.basecamp.activityRowContextMenu}
          onClose={() => setCtx(null)}
          entries={[
            {
              label: 'Preview',
              icon: OpenDestinationIcons.preview,
              onSelect: () => {
                selectEvent(ctx.event)
                setCtx(null)
              },
            },
            {
              label: 'Open',
              icon: OpenDestinationIcons.open,
              disabled: !eventTodoId(ctx.event) || !ctx.event.project_id,
              onSelect: () => {
                openEvent(ctx.event)
                setCtx(null)
              },
            },
            {
              label: 'Open in new tab',
              icon: OpenDestinationIcons.openInNewTab,
              disabled: !eventTodoId(ctx.event) || !ctx.event.project_id,
              onSelect: () => {
                openEvent(ctx.event, { openInNewTab: true })
                setCtx(null)
              },
            },
            {
              label: 'Open in Basecamp',
              icon: ExternalIcon,
              disabled: !eventTodoId(ctx.event) || !ctx.event.project_id,
              onSelect: () => {
                openEventInBasecamp(ctx.event)
                setCtx(null)
              },
            },
            { separator: true },
            panelSearchMenuAction(openActivitySearch),
          ]}
        />
      ) : null}
    </IndexSurfaceShell>
  )
}
